import { useCallback } from 'react';
import { Platform } from 'react-native';
import { request, PERMISSIONS, RESULTS, openSettings } from 'react-native-permissions';
import { useAppDispatch, useAppSelector } from '@/hooks/use.redux';
import { actionShowAlert } from '@/redux';
import { actionSetCameraPermission, actionSetMicPermission } from '@/redux/permissions.reducer';

const MIC_PERMISSION = PERMISSIONS.ANDROID.RECORD_AUDIO;
const CAMERA_PERMISSION = PERMISSIONS.ANDROID.CAMERA;

export function usePermissions() {
  const mic = useAppSelector((reducer) => reducer.permissionsReducer.mic);
  const camera = useAppSelector((reducer) => reducer.permissionsReducer.camera);
  const dispatch = useAppDispatch();

  const fetchPermissions = useCallback(
    async (type: 'mic' | 'camera_mic') => {
      // Keep a local tracking object so we don't have to wait for Redux state to flush during checks
      const permissions = {
        camera: camera.granted,
        mic: mic.granted,
      };

      const hasPermissions = () => permissions.mic && (type !== 'camera_mic' || permissions.camera);

      const displayFailure = () =>
        dispatch(
          actionShowAlert({
            type: 'fancy',
            mode: 'error',
            message: 'تلاش برای دریافت اجازه ها ناموفق بود ، لطفا برای برقراری تماس و ضبط صدا ، ابتدا اجازه های لازم را بدهید',
            // Browsers do not have an API to open OS settings, so we only pass this on native Android
            onYes: Platform.OS !== 'web' ? () => openSettings('application') : undefined,
          }),
        );

      if (!hasPermissions()) {
        // If Redux says we cannot ask again (BLOCKED state on Android), fail early and show the alert
        if ((!mic.granted && !mic.canAskAgain) || (type === 'camera_mic' && !camera.granted && !camera.canAskAgain)) {
          displayFailure();
          return false;
        }

        const requestPermissions = async () => {
          // --- WEB BROWSER LOGIC ---
          if (Platform.OS === 'web') {
            try {
              // Trigger the browser's native permission popup by requesting a media stream
              const stream = await navigator.mediaDevices.getUserMedia({
                audio: !permissions.mic,
                video: type === 'camera_mic' && !permissions.camera,
              });

              // Stop the tracks immediately so the red recording dot disappears from the browser tab
              stream.getTracks().forEach((track) => track.stop());

              // If it didn't throw an error, the user clicked "Allow"
              if (!permissions.mic) {
                permissions.mic = true;
                dispatch(actionSetMicPermission({ granted: true, canAskAgain: false }));
              }
              if (type === 'camera_mic' && !permissions.camera) {
                permissions.camera = true;
                dispatch(actionSetCameraPermission({ granted: true, canAskAgain: false }));
              }
              return true;
            } catch (error) {
              // User clicked "Block" or "Dismiss" on the browser prompt
              displayFailure();
              return false;
            }
          }

          // --- ANDROID LOGIC ---
          else {
            if (!permissions.mic) {
              const res = await request(MIC_PERMISSION);
              permissions.mic = res === RESULTS.GRANTED;

              // Sync result to Redux immediately so the UI doesn't lag behind
              dispatch(actionSetMicPermission({ granted: permissions.mic, canAskAgain: res !== RESULTS.BLOCKED }));
            }

            if (type === 'camera_mic' && !permissions.camera) {
              const res = await request(CAMERA_PERMISSION);
              permissions.camera = res === RESULTS.GRANTED;

              dispatch(actionSetCameraPermission({ granted: permissions.camera, canAskAgain: res !== RESULTS.BLOCKED }));
            }

            // Final verification in case they rejected the native Android prompt
            if (!hasPermissions()) {
              displayFailure();
              return false;
            }

            return true;
          }
        };

        return new Promise(async (resolve) => {
          dispatch(
            actionShowAlert({
              type: 'fancy',
              mode: 'info',
              message: 'برای برقراری تماس و ضبط صدا ابتدا باید اجازه دسترسی به دوربین و میکروفون را بدهید',
              onClose: () => requestPermissions().then(resolve),
            }),
          );
        });
      }

      return true;
    },
    [camera, mic, dispatch],
  );

  return {
    mic,
    camera,
    fetchPermissions,
    // Provide a dummy function for web so calling it doesn't crash, otherwise use native openSettings
    openAppSettings: Platform.OS === 'web' ? () => {} : openSettings,
  };
}
